import { cvApi } from '../services/api/cvApi';

/**
 * Transform a saved CV structure to match the repository list structure
 * @param {Object} savedCV - The CV object returned from save operation
 * @param {Object|null} existingCV - The existing CV in the store, if available
 * @returns {Object} The transformed CV object
 */
export const transformCVForStore = (savedCV, existingCV = null) => {
  // Increment version for existing CVs to simulate backend behavior
  const newVersion = existingCV && existingCV.version 
    ? existingCV.version + 1 
    : (savedCV.version || 1);

  const formatted = {
    id: savedCV.id,
    // ADR-109: the code is immutable — take it from the response, else keep the one already loaded
    code: savedCV.code || existingCV?.code || null,
    fullName: savedCV.employee?.fullName || '',
    version: newVersion,
    employee: savedCV.employee?.id ? {
      id: savedCV.employee.id,
      azureId: savedCV.employee.azureId,
      fullName: savedCV.employee.fullName
    } : null,
    position: savedCV.position || (existingCV ? existingCV.position : null),
    grade: savedCV.grade || (existingCV ? existingCV.grade : null),
    country: savedCV.country || (existingCV ? existingCV.country : null),
    city: savedCV.city || (existingCV ? existingCV.city : null),
    roles: savedCV.roles || (existingCV ? existingCV.roles : []),
    customClassification: savedCV.customClassification || (existingCV ? existingCV.customClassification : ''),
    useCustomClassification: savedCV.useCustomClassification || (existingCV ? existingCV.useCustomClassification : false),
    professionalSummary: savedCV.professionalSummary || (existingCV ? existingCV.professionalSummary : ''),
    skills: savedCV.skills || (existingCV ? existingCV.skills : []),
    employmentHistory: savedCV.employmentHistory || (existingCV ? existingCV.employmentHistory : []),
    education: savedCV.education || (existingCV ? existingCV.education : []),
    courses: savedCV.courses || (existingCV ? existingCV.courses : []),
    certifications: savedCV.certifications || (existingCV ? existingCV.certifications : []),
    languages: savedCV.languages || (existingCV ? existingCV.languages : []),
    // Keep original timestamps if available, otherwise use current date
    createdAt: savedCV.createdAt || (existingCV ? existingCV.createdAt : new Date().toISOString()),
    updatedAt: savedCV.updatedAt || new Date().toISOString(),
    // Add professionalDetails structure for CV cards compatibility
    professionalDetails: {
      position: savedCV.position || (existingCV ? existingCV.position : null),
      grade: savedCV.grade || (existingCV ? existingCV.grade : null),
      country: savedCV.country || (existingCV ? existingCV.country : null),
      city: savedCV.city || (existingCV ? existingCV.city : null),
      roles: savedCV.roles || (existingCV ? existingCV.roles : []),
      customClassification: savedCV.customClassification || (existingCV ? existingCV.customClassification : ''),
      useCustomClassification: savedCV.useCustomClassification || (existingCV ? existingCV.useCustomClassification : false)
    }
  };
  
  // Use the formatCVForCompletion function to calculate the completion
  const withCompletion = formatCVForCompletion(formatted);
  
  return {
    ...formatted,
    completion: withCompletion.completion
  };
};

/**
 * Format a CV for completion calculation
 * @param {Object} cv - The CV object to format
 * @returns {Object} The CV object with completion data
 */
export const formatCVForCompletion = (cv) => {
  if (!cv) return { completion: {} };
  
  return {
    ...cv,
    completion: {
      hasPersonalDetails: Boolean(
        cv.employee?.fullName &&
        (cv.position || (cv.useCustomClassification && cv.customClassification))
      ),
      professionalSummaryLength: cv.professionalSummary ? cv.professionalSummary.length : 0,
      generalSkillsCount: cv.skills ? cv.skills.length : 0,
      employmentHistoryCount: cv.employmentHistory ? cv.employmentHistory.length : 0,
      educationCount: cv.education ? cv.education.length : 0,
      coursesCount: cv.courses ? cv.courses.length : 0,
      certificationsCount: cv.certifications ? cv.certifications.length : 0,
      languagesCount: cv.languages ? cv.languages.length : 0
    }
  };
};

/**
 * Common function to handle CV fetching with pagination
 * @param {Function} fetchFunction - The API function to call
 * @param {Object} options - Additional options for the fetch function including:
 *   - limit: number of items per page
 *   - cursor: pagination cursor
 *   - orderBy: field to order by
 *   - orderDirection: asc or desc
 *   - search: search term
 *   - positionIds: array of position IDs to filter by
 * @returns {Promise<Object|null>} The API response or null if there was an error
 */
export const fetchCVsWithPagination = async (fetchFunction, options = {}) => {
  try {
    const response = await fetchFunction(options);
    return response;
  } catch (err) {
    console.error('Error fetching CVs');
    return null;
  }
};

/**
 * Set default ordering values based on view type
 * @param {string} viewType - The view type ('all' or 'employee')
 * @returns {Object} The default ordering object
 */
export const getDefaultOrdering = (viewType) => {
  const DEFAULT_ORDERING = {
    all: { orderBy: 'fullName', orderDirection: 'asc' },
    employee: { orderBy: 'updatedAt', orderDirection: 'desc' }
  };
  
  return DEFAULT_ORDERING[viewType] || DEFAULT_ORDERING.all;
}; 